import type { Boundaries } from "./parse";
import { Template as TemplateImpl } from "./template";

export type { Boundaries as TemplateBoundaries } from "./parse";
export type {
	TemplateInterpolateOptions,
	TemplateInterpolateOptionsWithFallback,
	TemplateVariableValue,
	TemplateVariables,
} from "./template";

const DEFAULT_OPTIONS_LEFT = "${";
const DEFAULT_OPTIONS_RIGHT = "}";

/**
 * The template monad.
 * It is a data structure that explicitly represents a string template with placeholders and provides methods to manipulate and interpolate it.
 *
 * A template carries its source string and a set of predefined variables.
 * The variables can be added/overridden using the `addVariables` method,
 * so that the source is interpolated with the latest set of variables.
 *
 * A template is defined by its boundaries, which are the strings that denote the start and end of a placeholder,
 * and by its irregular value type, which if present interrupts interpolation when a variable is missing.
 *
 * @template TBoundaries - The tuple of left and right boundary strings, defaults to ["${", "}"].
 * @template TIrregularVariable - The type of irregular variable values, can be either nothing (`never`) or `null`, defaults to `null`.
 *
 * @example
 * type DefaultTemplate = Template;
 * //                     ^? Template<["${", "}"], never>
 *
 * type CustomBoundaries = Template<["{{", "}}"]>;
 * //                      ^? Template<["{{", "}}"], never>
 *
 * type WithIrregular = Template<["{", "}"], null>;
 * //                   ^? Template<["{", "}"], null>
 */
export type Template<
	TBoundaries extends Boundaries = [typeof DEFAULT_OPTIONS_LEFT, typeof DEFAULT_OPTIONS_RIGHT],
	TIrregularVariable extends null = never,
> = TemplateImpl<TBoundaries, TIrregularVariable>;

/**
 * A factory to create a Template with default boundaries ["${", "}"].
 *
 * @template TIrregularVariable - The type of irregular variable values, can be either nothing (`never`) or `null`, defaults to `null`.
 * @param source - The template string containing placeholders.
 * @returns A Template instance with the specified source and default boundaries.
 */
// biome-ignore lint/suspicious/noRedeclare: intentional declaration merging of Template type and factory function
export function Template<TIrregularVariable extends null = never>(
	source: string,
): TemplateImpl<[typeof DEFAULT_OPTIONS_LEFT, typeof DEFAULT_OPTIONS_RIGHT], TIrregularVariable>;

/**
 * A factory to create a Template with custom boundaries.
 *
 * @template TIrregularVariable - The type of irregular variable values, can be either nothing (`never`) or `null`, defaults to `null`.
 * @param source - The template string containing placeholders.
 * @param left - The left boundary string.
 * @param right - The right boundary string.
 * @returns A Template instance with the specified source and boundaries.
 */
// biome-ignore lint/suspicious/noRedeclare: intentional declaration merging of Template type and factory function
export function Template<
	TLeftBoundary extends string,
	TRightBoundary extends string,
	TIrregularVariable extends null = never,
>(
	source: string,
	left: TLeftBoundary,
	right: TRightBoundary,
): TemplateImpl<[TLeftBoundary, TRightBoundary], TIrregularVariable>;

export function Template(
	source: string,
	left = DEFAULT_OPTIONS_LEFT,
	right = DEFAULT_OPTIONS_RIGHT,
): TemplateImpl<Boundaries, null> {
	return new TemplateImpl(source, {}, [left, right]);
}
